import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, UploadTask } from 'firebase/storage';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const OnboardingKyc: React.FC = () => {
  const navigate = useNavigate();
  const { firebaseUser, user, refreshUserData, logout } = useAuth();

  // Prefill from existing KYC data (if rejected) so painter only fixes problematic fields
  const [realName, setRealName] = useState(user?.kyc?.realName || user?.name || '');
  const [aadhaarNumber, setAadhaarNumber] = useState(user?.kyc?.aadhaarNumber || '');
  const [panNumber, setPanNumber] = useState(user?.kyc?.panNumber || '');
  const [aadhaarFile, setAadhaarFile] = useState<File | null>(null);
  const [panFile, setPanFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [aadhaarProgress, setAadhaarProgress] = useState(0);
  const [panProgress, setPanProgress] = useState(0);

  // File validation constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const UPLOAD_TIMEOUT_MS = 60000; // 60 seconds timeout
  const MAX_RETRIES = 2; // Maximum retry attempts

  // Validate and sanitize file
  const validateFile = (file: File, fileType: 'aadhaar' | 'pan'): { valid: boolean; error?: string } => {
    // Check file type (MIME type)
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Please upload an image file (JPEG, PNG, or WebP)' };
    }

    // Check specific MIME types
    if (!ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
      return { valid: false, error: 'Only JPEG, PNG, and WebP images are allowed' };
    }

    // Check file extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp'];
    if (!ext || !allowedExts.includes(ext)) {
      return { valid: false, error: 'File must have .jpg, .jpeg, .png, or .webp extension' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File size must be less than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB` };
    }

    return { valid: true };
  };

  // Sanitize file name
  const sanitizeFileName = (fileName: string): string => {
    // Remove special characters and keep only alphanumeric, dots, hyphens, underscores
    const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    // Limit length
    return sanitized.length > 100 ? sanitized.substring(0, 100) : sanitized;
  };

  // Get user-friendly error message
  const getErrorMessage = (error: any): string => {
    if (error.code === 'permission-denied') {
      return 'You do not have permission to perform this action. Please contact support.';
    }
    if (error.code === 'storage/quota-exceeded') {
      return 'Storage quota exceeded. Please contact support.';
    }
    if (error.code === 'storage/unauthorized') {
      return 'Upload permission denied. Please contact support.';
    }
    if (error.code === 'storage/canceled') {
      return 'Upload was canceled. Please try again.';
    }
    if (error.message?.includes('timeout') || error.message?.includes('network')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.message?.includes('exceeds') || error.message?.includes('size')) {
      return 'File size too large. Please compress images before uploading.';
    }
    return error.message || 'An error occurred. Please try again.';
  };

  // Upload file with timeout, retry, and progress tracking
  const uploadFileWithProgress = async (
    file: File,
    path: string,
    fileLabel: string,
    progressSetter: (progress: number) => void,
    retryCount = 0
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(ref(storage, path), file);
      
      // Set timeout
      const timeoutId = setTimeout(() => {
        uploadTask.cancel();
        reject(new Error(`Upload timeout: ${fileLabel} upload took too long. Please check your internet connection.`));
      }, UPLOAD_TIMEOUT_MS);

      // Track upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          progressSetter(progress);
        },
        (error) => {
          clearTimeout(timeoutId);
          // Retry on network errors
          if (retryCount < MAX_RETRIES && (
            error.code === 'storage/retry-limit-exceeded' ||
            error.message?.includes('network') ||
            error.message?.includes('timeout')
          )) {
            setTimeout(() => {
              uploadFileWithProgress(file, path, fileLabel, progressSetter, retryCount + 1)
                .then(resolve)
                .catch(reject);
            }, 1000 * (retryCount + 1)); // Exponential backoff
          } else {
            reject(error);
          }
        },
        async () => {
          clearTimeout(timeoutId);
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleAadhaarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setAadhaarFile(null);
      return;
    }

    const validation = validateFile(file, 'aadhaar');
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear input
      setAadhaarFile(null);
      return;
    }

    setError(''); // Clear previous errors
    setAadhaarFile(file);
  };

  const handlePanFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setPanFile(null);
      return;
    }

    const validation = validateFile(file, 'pan');
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      e.target.value = ''; // Clear input
      setPanFile(null);
      return;
    }

    setError(''); // Clear previous errors
    setPanFile(file);
  };

  // Load data from Firestore when user/KYC data is available
  useEffect(() => {
    if (user) {
      // Load KYC data if exists
      if (user.kyc) {
        setRealName(prev => prev || user.kyc!.realName || user.name || '');
        setAadhaarNumber(prev => prev || user.kyc!.aadhaarNumber || '');
        setPanNumber(prev => prev || user.kyc!.panNumber || '');
      } else {
        // If no KYC data, use name from user profile
        setRealName(prev => prev || user.name || '');
      }
    }
  }, [user]);

  // Update combined progress when individual progresses change
  useEffect(() => {
    if (saving && (aadhaarFile || panFile)) {
      const totalFiles = (aadhaarFile ? 1 : 0) + (panFile ? 1 : 0);
      if (totalFiles > 0) {
        const combinedProgress = ((aadhaarFile ? aadhaarProgress : 0) + (panFile ? panProgress : 0)) / totalFiles;
        setUploadProgress(combinedProgress);
        
        if (totalFiles === 2) {
          setUploadStatus(`Uploading documents... ${Math.round(combinedProgress)}%`);
        } else if (aadhaarFile) {
          setUploadStatus(`Uploading Aadhaar photo... ${Math.round(aadhaarProgress)}%`);
        } else if (panFile) {
          setUploadStatus(`Uploading PAN photo... ${Math.round(panProgress)}%`);
        }
      }
    }
  }, [aadhaarProgress, panProgress, aadhaarFile, panFile, saving]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) return;
    
    // Check if user is blocked
    if (user?.kyc?.blockPermanent) {
      setError('Your KYC submission is permanently blocked. Please contact support.');
      return;
    }
    if (user?.kyc?.blockUntil) {
      const blockDate = user.kyc.blockUntil;
      if (blockDate > new Date()) {
        const daysLeft = Math.ceil((blockDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        setError(`Your account is temporarily blocked. You can resubmit KYC after ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`);
        return;
      }
    }

    // For resubmission: allow reuse of existing photos if new files not uploaded
    const hasExistingAadhaar = !!user?.kyc?.aadhaarPhotoURL;
    const hasExistingPan = !!user?.kyc?.panPhotoURL;

    if (!realName.trim() || aadhaarNumber.replace(/\D/g, '').length !== 12 || panNumber.trim().length < 8) {
      setError('Please fill valid KYC details');
      return;
    }

    if (!aadhaarFile && !hasExistingAadhaar) {
      setError('Please attach Aadhaar photo');
      return;
    }

    if (!panFile && !hasExistingPan) {
      setError('Please attach PAN photo');
      return;
    }
    setSaving(true);
    setError(''); // Clear previous errors
    setUploadProgress(0);
    setUploadStatus('');
    
    try {
      const uid = firebaseUser.uid;

      // Start with existing URLs if present; override only when new files are uploaded
      let aadhaarURL = user?.kyc?.aadhaarPhotoURL || '';
      let panURL = user?.kyc?.panPhotoURL || '';

      // Reset progress states
      setAadhaarProgress(0);
      setPanProgress(0);
      setUploadProgress(0);

      // Prepare parallel uploads
      const uploadPromises: Promise<void>[] = [];

      // Upload new Aadhaar if provided
      if (aadhaarFile) {
        const ext = aadhaarFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const aadhaarPath = `users/${uid}/kyc/aadhaar-${Date.now()}.${ext}`;
        
        uploadPromises.push(
          uploadFileWithProgress(aadhaarFile, aadhaarPath, 'Aadhaar photo', setAadhaarProgress)
            .then((url) => {
              aadhaarURL = url;
              setAadhaarProgress(100);
            })
            .catch((uploadErr: any) => {
              setAadhaarProgress(0);
              throw new Error(`Aadhaar upload failed: ${getErrorMessage(uploadErr)}`);
            })
        );
      }

      // Upload new PAN if provided
      if (panFile) {
        const ext = panFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const panPath = `users/${uid}/kyc/pan-${Date.now()}.${ext}`;
        
        uploadPromises.push(
          uploadFileWithProgress(panFile, panPath, 'PAN photo', setPanProgress)
            .then((url) => {
              panURL = url;
              setPanProgress(100);
            })
            .catch((uploadErr: any) => {
              setPanProgress(0);
              throw new Error(`PAN upload failed: ${getErrorMessage(uploadErr)}`);
            })
        );
      }

      // Execute all uploads in parallel
      if (uploadPromises.length > 0) {
        const totalFiles = uploadPromises.length;
        setUploadStatus(`Uploading ${totalFiles} document${totalFiles > 1 ? 's' : ''}...`);
        
        await Promise.all(uploadPromises);
        
        // Ensure both are at 100% after completion
        if (aadhaarFile) setAadhaarProgress(100);
        if (panFile) setPanProgress(100);
        setUploadProgress(100);
        setUploadStatus('Upload complete! Saving...');
      }
      
      // Use field-level updates to properly increment submitCount
      const kycUpdateData = {
        'kyc.realName': realName,
        'kyc.aadhaarNumber': aadhaarNumber,
        'kyc.panNumber': panNumber,
        'kyc.aadhaarPhotoURL': aadhaarURL,
        'kyc.panPhotoURL': panURL,
        'kyc.status': 'submitted',
        'kyc.submittedAt': serverTimestamp(),
        'kyc.submitCount': increment(1),
        'kyc.rejectReason': '',
        'kyc.needsBankResubmit': (user?.kyc?.submitCount || 0) > 0, // Only true for resubmissions
      };
      
      setUploadStatus('Saving KYC details...');
      await updateDoc(doc(db, 'users', uid), kycUpdateData);
      await refreshUserData?.();
      setUploadStatus('Success! Redirecting...');
      navigate('/onboarding/bank', { replace: true });
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
      setUploadProgress(0);
      setAadhaarProgress(0);
      setPanProgress(0);
      setUploadStatus('');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel registration? You will be logged out.')) {
      await logout();
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="bg-[#ee4238] text-white px-6 pt-14 pb-8 rounded-b-[28px] text-center">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleCancel}
            className="text-white/90 hover:text-white transition-colors flex items-center gap-1 sm:gap-2 px-2 sm:px-0 py-1 sm:py-0"
          >
            <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm font-medium">Cancel</span>
          </button>
          <div className="flex-1"></div>
        </div>
        <h1 className="text-2xl font-black">KYC Details</h1>
        <p className="text-white/90 mt-2 text-sm">Mandatory for withdrawals. Status will show as Pending after submit.</p>
        
        {/* Progress Steps */}
        <div className="mt-6 flex justify-center items-center space-x-4">
          {/* Step 1 - Personal (Completed) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">Personal</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white"></div>
          
          {/* Step 2 - KYC (Active) */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <span className="text-[#ee4238] text-xs font-bold">2</span>
              </div>
            </div>
            <span className="text-white text-xs font-medium mt-1">KYC</span>
          </div>
          
          {/* Connector Line */}
          <div className="w-8 h-0.5 bg-white/30"></div>
          
          {/* Step 3 - Bank */}
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
              <span className="text-white/60 text-xs font-bold">3</span>
            </div>
            <span className="text-white/60 text-xs font-medium mt-1">Bank</span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 w-full bg-white/20 rounded-full h-1">
          <div className="bg-white h-1 rounded-full" style={{ width: '66%' }}></div>
        </div>
      </div>

      {/* Blocking banner (if account is blocked) */}
      {(user?.kyc?.blockPermanent || (user?.kyc?.blockUntil && user.kyc.blockUntil > new Date())) && (
        <div className="px-6 mt-4">
          <div className="rounded-2xl border-2 border-red-500 bg-red-100 p-4">
            <div className="text-red-800 font-extrabold text-base mb-2 flex items-center gap-2">
              🚫 Account Blocked
            </div>
            <div className="text-red-700 text-sm font-semibold">
              {user?.kyc?.blockPermanent 
                ? 'Your KYC submission is permanently blocked. Please contact support for assistance.'
                : user?.kyc?.blockUntil && (() => {
                    const daysLeft = Math.ceil((user.kyc.blockUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                    return `Your account is temporarily blocked. You can resubmit KYC after ${daysLeft} day${daysLeft > 1 ? 's' : ''} (${user.kyc.blockUntil.toLocaleDateString()}).`;
                  })()
              }
            </div>
          </div>
        </div>
      )}

      {/* Rejection banner (if previously rejected but not blocked) */}
      {user?.kyc?.status === 'rejected' && !user?.kyc?.blockPermanent && (!user?.kyc?.blockUntil || user.kyc.blockUntil <= new Date()) && (
        <div className="px-6 mt-4">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <div className="text-red-700 font-extrabold text-sm mb-1">KYC Rejected - Attempt #{(user?.kyc?.submitCount || 0) + 1}</div>
            <div className="text-red-700/90 text-sm">
              {user?.kyc?.rejectReason ? user.kyc.rejectReason : 'Your KYC was rejected. Please correct the details and resubmit.'}
            </div>
          </div>
        </div>
      )}

      <form id="kycForm" onSubmit={handleSubmit} className="flex-1 p-6 space-y-4 pb-28">
        {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>}
        
        {/* Upload Progress Indicator */}
        {saving && uploadStatus && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-800">{uploadStatus}</span>
              {uploadProgress > 0 && <span className="text-sm font-bold text-blue-600">{Math.round(uploadProgress)}%</span>}
            </div>
            {uploadProgress > 0 && (
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Real Name *</label>
          <input value={realName} onChange={e => setRealName(e.target.value)} className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Aadhaar Number *</label>
          <input value={aadhaarNumber} onChange={e => setAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0,12))} maxLength={12} inputMode="numeric" pattern="[0-9]*" className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Aadhaar Photo *</label>
          <input 
            type="file" 
            accept="image/jpeg,image/jpg,image/png,image/webp" 
            onChange={handleAadhaarFileChange}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]"
          />
          <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Formats: JPEG, PNG, WebP</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">PAN Number *</label>
          <input value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase().slice(0,10))} className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">PAN Photo *</label>
          <input 
            type="file" 
            accept="image/jpeg,image/jpg,image/png,image/webp" 
            onChange={handlePanFileChange}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:ring-2 focus:ring-[#ee4238]"
          />
          <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Formats: JPEG, PNG, WebP</p>
        </div>

        <div className="h-4"></div>
      </form>

      {/* Fixed bottom action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-6 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/quick-profile', { replace: false })}
            disabled={saving}
            className="flex-1 bg-gray-200 text-gray-700 py-4 px-6 font-extrabold rounded-2xl disabled:opacity-50 hover:bg-gray-300 transition-colors"
          >
            Previous
          </button>
          <button 
            type="submit" 
            form="kycForm" 
            disabled={
              saving ||
              !realName.trim() ||
              aadhaarNumber.replace(/\D/g, '').length !== 12 ||
              panNumber.trim().length < 8 ||
              // Require Aadhaar/PAN photo only if no existing photo URL in Firestore
              (!aadhaarFile && !user?.kyc?.aadhaarPhotoURL) ||
              (!panFile && !user?.kyc?.panPhotoURL) ||
              user?.kyc?.blockPermanent ||
              (user?.kyc?.blockUntil && user.kyc.blockUntil > new Date())
            } 
            className="flex-1 bg-[#ee4238] text-white py-4 px-6 font-extrabold rounded-2xl disabled:opacity-50"
          >
            {saving 
              ? (uploadStatus || 'Submitting...')
              : (user?.kyc?.blockPermanent || (user?.kyc?.blockUntil && user.kyc.blockUntil > new Date()))
              ? 'Blocked'
              : 'Submit & Continue'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingKyc;


